import { createSlice } from '@reduxjs/toolkit'
import name from './name'
import { DEFAULT_NOTIFICATION_PREFERENCES } from '../../components/notification-center/constants'

const initialState = {
  items: [],
  // Server-owned unread count. Stays `null` until a payload carries one, and
  // the selector falls back to counting `items` — the badge has to be right
  // both before pagination exists and after, when `items` is only a page.
  unreadCount: null,
  total: 0,
  loading: false,
  // Separate from `loading` so appending a page never blanks the list that is
  // already on screen (NC-31).
  loadingMore: false,
  // Which of the two the in-flight request is, so `setNotifications` knows
  // whether its payload replaces the list or extends it. The event bus gives
  // one `notificationsGet` response either way.
  appending: false,
  error: null,
  hasLoaded: false,
  // Set when an optimistic write failed, so the hook re-fetches instead of
  // leaving the user looking at a dot that only cleared on this client.
  needsResync: false,
  // Which types the user wants in-app (NC-40). Seeded from the profile once it
  // carries them; everything on until then.
  preferences: DEFAULT_NOTIFICATION_PREFERENCES
}

const countUnread = items => items.filter(item => !item.read).length

const notificationsSlice = createSlice({
  name,
  initialState,
  reducers: {
    setLoading: state => {
      state.loading = true
      state.appending = false
      state.error = null
      // A fetch is in flight — whatever went out of sync is about to be fixed.
      state.needsResync = false
    },
    setLoadingMore: state => {
      state.loadingMore = true
      state.appending = true
      state.error = null
    },
    setNotifications: (state, { payload }) => {
      const incoming = Array.isArray(payload) ? payload : payload?.items || []

      if (state.appending) {
        // Ids already held win: a row read since the first page was fetched
        // must not be reset by an overlapping page.
        const held = new Set(state.items.map(item => item.id))
        state.items = state.items.concat(incoming.filter(item => !held.has(item.id)))
      } else {
        state.items = incoming
      }

      state.unreadCount = typeof payload?.unreadCount === 'number'
        ? payload.unreadCount
        : null
      state.total = typeof payload?.total === 'number'
        ? payload.total
        : state.items.length
      state.loading = false
      state.loadingMore = false
      state.appending = false
      state.error = null
      state.hasLoaded = true
    },
    setError: (state, { payload }) => {
      const wasAppending = state.appending
      state.loading = false
      state.loadingMore = false
      state.appending = false
      // A page that failed to append is not a broken screen: the list already
      // on show stays and the toast carries the news. Only a failed first load
      // becomes an error state.
      if (wasAppending && state.items.length > 0) return
      state.error = payload
      state.hasLoaded = true
    },
    setNeedsResync: state => {
      state.needsResync = true
    },
    // Whole set, e.g. seeded from the profile on load.
    setPreferences: (state, { payload }) => {
      state.preferences = { ...DEFAULT_NOTIFICATION_PREFERENCES, ...payload }
    },
    // One switch. Optimistic like the read flags — a rejected write is put back
    // by the caller, which is the only place that knows the previous value.
    setPreference: (state, { payload }) => {
      const { key, value } = payload
      if (!(key in state.preferences)) return
      state.preferences[key] = value
    },
    // Optimistic — the server round trip is fire-and-forget, matching how
    // bookmarks/likes already behave elsewhere in the app. `unreadCount` is
    // kept in step by hand so a server-sent count doesn't go stale between
    // fetches; a failed write raises `needsResync` and the truth comes back.
    markRead: (state, { payload }) => {
      const id = payload?.id ?? payload
      const wasUnread = state.items.some(item => item.id === id && !item.read)
      state.items = state.items.map(item =>
        item.id === id ? { ...item, read: true } : item
      )
      if (wasUnread && state.unreadCount !== null) {
        state.unreadCount = Math.max(0, state.unreadCount - 1)
      }
    },
    markAllRead: state => {
      state.items = state.items.map(item =>
        item.read ? item : { ...item, read: true }
      )
      // Only the loaded pages were marked, but "mark all read" means all of it —
      // the server clears the rest, and a re-fetch would only confirm zero.
      if (state.unreadCount !== null) state.unreadCount = 0
    },
    dismiss: (state, { payload }) => {
      const id = payload?.id ?? payload
      const removed = state.items.find(item => item.id === id)
      state.items = state.items.filter(item => item.id !== id)
      if (removed && !removed.read && state.unreadCount !== null) {
        state.unreadCount = Math.max(0, state.unreadCount - 1)
      }
      if (removed && state.total > 0) state.total -= 1
    }
  }
})

export const {
  setLoading,
  setLoadingMore,
  setNotifications,
  setError,
  setNeedsResync,
  setPreferences,
  setPreference,
  markRead,
  markAllRead,
  dismiss
} = notificationsSlice.actions

export const { reducer } = notificationsSlice
export { countUnread }
